'use client'

import React, { useCallback } from 'react'
import Link from 'next/link'
import { useRouter, usePathname } from 'next/navigation'
import clsx from 'clsx'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@/store/user/slice'
import { Story } from '@/types/story'
import CardHeader from '@/components/Shared/Card/CardHeader'
import GroupOnly from '@/components/Shared/Icon/GroupOnly'
import cardStyles from '@/components/Shared/Card/styles.module.scss'
import styles from './styles.module.scss'

interface ArticleCardProps {
  article: Story
  index?: number
  paramSrag?: string
  bigCard?: boolean
  used?: string
  extraUsed?: string
  recommend?: boolean
  onSlideClick?: () => void
  bookId?: number | null
  groupId?: number | null
}

export default function ArticleCard({
  article,
  paramSrag = '',
  bigCard = false,
  used = '',
  extraUsed = '',
  recommend = false,
  onSlideClick,
  bookId = null,
  groupId = null,
}: ArticleCardProps) {
  const router = useRouter()
  const pathname = usePathname()
  const currentUser = useSelector(selectCurrentUser)

  const articleLink = React.useMemo(() => {
    if (bookId) {
      // `bookId` を付与
      return `${article.path}?bookId=${bookId}`
    } else if (groupId) {
      // `groupId` を付与
      return `${article.path}?groupId=${groupId}`
    } else if (paramSrag) {
      // `category` を付与
      return `${article.path}?category=${paramSrag}`
    }
    // 条件に該当しない場合はそのままのパスを返す
    return article.path
  }, [article.path, bookId, groupId, paramSrag])

  // KARTEカスタムイベント用に現在のpathに応じたnameを返却
  const determineFrom = useCallback((): string => {
    const currentPath = pathname

    const routeMap: Record<string, (p: string) => boolean> = {
      top: (p) => p === '/',
      mypage: (p) => p.startsWith('/members/mypage'),
      story: (p) => p.includes('/stories/') && !p.endsWith('/stories'),
      knowhow: (p) => p.includes('/knowhows/') && !p.endsWith('/knowhows'),
    }

    return Object.keys(routeMap).find((key) => routeMap[key](currentPath)) || 'unknown'
  }, [pathname])

  const handleArticleClick = useCallback(() => {
    requestAnimationFrame(() => {
      // Propのスライドクリックイベントをcall
      if (onSlideClick) onSlideClick()
    })

    // 非同期処理にしてINP改善
    setTimeout(() => {
      if (typeof window !== 'undefined' && (window as any).krt && pathname) {
        const from = determineFrom()
        const fromUrl = pathname
        const distUrl = articleLink
        const memberId = currentUser?.id?.toString() || ''

        ;(window as any).krt('send', 'click_recommend_article', {
          from,
          fromUrl,
          distUrl,
          memberId,
        })
      }
    }, 0)
  }, [onSlideClick, pathname, determineFrom, articleLink, currentUser?.id])

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // ctrl + click / cmd + click の挙動
      if (event.ctrlKey || event.metaKey) {
        window.open(articleLink, '_blank')
        return
      }
      // nuxt-linkの内部処理をキャンセルすることでvue-routerの同期処理をスキップ
      event.preventDefault()
      // recommendの場合,KARTEのカスタムイベント発火
      if (recommend) handleArticleClick()
      router.push(articleLink)
    },
    [articleLink, recommend, handleArticleClick, router],
  )

  const shouldDisplayFooter =
    !['company'].includes(extraUsed || '') && !['company'].includes(used || '')

  const getCardClasses = useCallback(() => {
    const classes = [cardStyles.cardBase, styles.card]

    if (bigCard) classes.push(styles.bigCard)
    if (used === 'top-carousel') classes.push(styles.topCarousel)
    if (used === 'top-category') classes.push(styles.topCategory)
    if (used === 'top-small-category') classes.push(styles.topSmallCategory)
    if (used === 'carousel') classes.push(styles.carousel)
    if (used === 'full') classes.push(styles.full)

    // その他のusedクラス（company, history, bookshelf等）
    if (
      used &&
      used !== 'top-carousel' &&
      used !== 'top-category' &&
      used !== 'top-small-category' &&
      used !== 'carousel' &&
      used !== 'full' &&
      styles[used]
    ) {
      classes.push(styles[used])
    }

    return classes.filter(Boolean).join(' ')
  }, [bigCard, used])

  return (
    <article className={getCardClasses()}>
      <Link className={cardStyles.cardLink} href={articleLink} onClick={handleClick} />
      <div className={styles.cardContentsContainer}>
        <div className={styles.cardImg}>
          {!article.eyecatchImageUrl && <div className={styles.dummyImageArea} />}
          {article.eyecatchImageUrl && (
            <img
              src={article.eyecatchImageUrl}
              width={800}
              height={450}
              className={styles.eyecatchImage}
              alt={article.title}
              loading={used === 'top-carousel' ? 'eager' : 'lazy'}
            />
          )}
        </div>
        <div className={styles.cardContents}>
          {used !== 'top-carousel' && (
            <div className={styles.cardInformation}>
              <CardHeader
                used={used}
                openDate={article.openDate}
                articleId={article.id}
                articleType={article.storyTypeJa}
                articleLink={articleLink}
              />
            </div>
          )}
          <div className={styles.contentsBody}>
            <div className={styles.articleTitle}>{article.title}</div>
            {article.intro && (
              <p
                className={clsx(styles.articleIntro, {
                  [styles.disnon]: used !== 'history' && !bigCard,
                })}
              >
                {article.intro}
              </p>
            )}
            {shouldDisplayFooter && (
              <div className={cardStyles.cardFooter}>
                <div className={styles.articleCompanyName}>{article.company?.name || ''}</div>
                {article.publishLevel === 1 && (
                  <div className={styles.groupOnlyWrapper}>
                    <GroupOnly />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  )
}
